import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { CheckCircle, XCircle } from 'react-feather';
import { getOrderById, checkUnavailableProducts } from '../../utils/apiCalls';
import React, { useEffect, useState } from "react";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Loader as RLoader } from "../reporting/ReportingShared";
import { describeStatusChange, findCancellationEntry } from "../../utils/orderStatusActor";
import {
  BLOCK_REASON_LABELS,
  LOYALTY_SKIP_LABELS,
  LOYALTY_POLICY_LABELS,
  formatCap,
  formatDiscount,
} from "../membership/membershipConstants";

const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "canceled") return { bg: C.coralLight, fg: C.coral };
  if (s === "pending") return { bg: C.orangeLight, fg: C.orange };
  if (s === "delivered" || s === "accepted") return { bg: C.greenLight, fg: C.green };
  if (s === "shipped" || s === "ready") return { bg: C.blueLight, fg: C.blue };
  return { bg: C.bg, fg: C.textMed };
};

const StatusPill = ({ status }) => {
  const c = statusColor(status);
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "--";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.fg,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.fg }} />
      {label}
    </span>
  );
};

const InfoCell = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: C.textLight,
      textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4,
    }}>{label}</div>
    <div style={{
      fontSize: 13, fontWeight: 600, color: C.text,
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }} title={typeof value === "string" ? value : undefined}>
      {value ?? "--"}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: 13, fontWeight: 700, color: C.text,
    textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12,
  }}>{children}</div>
);

const Card = ({ children, style }) => (
  <div style={{
    background: C.white, borderRadius: 12, padding: "18px 20px",
    boxShadow: C.shadow, border: `1px solid ${C.border}`,
    marginBottom: 16, ...style,
  }}>
    {children}
  </div>
);

const JsonBlock = ({ value, empty = "No data available." }) => {
  if (value == null || value === "" || (typeof value === "object" && Object.keys(value).length === 0)) {
    return <div style={{ color: C.textMed, fontSize: 13, padding: "8px 0" }}>{empty}</div>;
  }
  let pretty;
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    pretty = JSON.stringify(parsed, null, 2);
  } catch {
    pretty = String(value);
  }
  return (
    <pre style={{
      margin: 0, whiteSpace: "pre-wrap", fontSize: 12,
      background: C.bg, color: C.text,
      padding: "14px 16px", borderRadius: 8,
      border: `1px solid ${C.border}`,
      maxHeight: 360, overflow: "auto",
    }}>{pretty}</pre>
  );
};

const SourceBadge = ({ label, tone }) => (
  <span style={{
    display: "inline-block", padding: "3px 10px", borderRadius: 6,
    fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
    background: tone.bg, color: tone.fg,
  }}>
    {label}
  </span>
);

// "Who did this" for one history row: the origin (branch / system / customer …) plus the person
// logged in on that device at the time, when we know it.
const ChangedByCell = ({ history }) => {
  const { sourceLabel, tone, actorName, actorDetails } = describeStatusChange(history);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <SourceBadge label={sourceLabel} tone={tone} />
      {actorName ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{actorName}</div>
      ) : null}
      {actorDetails.length ? (
        <div style={{ fontSize: 11, color: C.textLight }}>{actorDetails.join(" · ")}</div>
      ) : null}
    </div>
  );
};

// Canceled orders lead with this, so nobody has to open the Status Updates tab to find out
// who canceled and why.
const CancellationBanner = ({ orderData }) => {
  const entry = findCancellationEntry(orderData?.OrderStatusHistories);
  if (!entry) return null;

  const { sourceLabel, tone, actorName, actorDetails, reason } = describeStatusChange(entry);

  return (
    <div style={{
      background: C.coralLight, border: `1px solid ${C.coral}33`,
      borderLeft: `4px solid ${C.coral}`, borderRadius: 10,
      padding: "14px 18px", marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: C.coral, textTransform: "uppercase", letterSpacing: 0.6 }}>
          Order canceled
        </span>
        <SourceBadge label={sourceLabel} tone={tone} />
        {entry?.createdAt ? (
          <span style={{ fontSize: 12, color: C.textMed }}>{new Date(entry.createdAt).toLocaleString()}</span>
        ) : null}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
        {reason || "No reason was recorded."}
      </div>
      {(actorName || actorDetails.length) ? (
        <div style={{ fontSize: 12, color: C.textMed, marginTop: 6 }}>
          By {actorName || "unidentified user"}
          {actorDetails.length ? ` · ${actorDetails.join(" · ")}` : ""}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Did this order ever reach a kitchen?
 *
 * Only self-order screens answer this. Every other order type reaches a kitchen through the POS,
 * which has its own "Received By POS" row above; a kiosk order has no cashier in the loop and pushes
 * its own ticket, so the delivery report it sends back is the only record that the food was ever
 * asked for.
 *
 * Worth showing separately from `status` because the two genuinely disagree: an order can read
 * `ready` — the kitchen bumped what it was given — while a line of it never reached any kitchen at
 * all. That is the case this section exists to make visible without anyone querying the database.
 */
const KitchenDelivery = ({ orderData }) => {
  const kitchen = orderData?.orderInformation?.kitchen;
  if (orderData?.orderNature !== "kiosk") return null;

  const stations = Array.isArray(kitchen?.stations) ? kitchen.stations : [];
  const unrouted = Number(kitchen?.unroutedLines) || 0;
  const deliveredAt = orderData?.kdsDeliveredAt;

  // Nothing reported at all: the screen never got as far as telling us how the push went. Shown
  // rather than hidden — for a paid order it is the most alarming state of the three.
  const nothingReported = !kitchen && !deliveredAt;

  // Amber, not `C.coral`, for every attention state below. `coral` is the teal brand colour after the
  // rebrand (#009689) — the panel still uses it to mean "canceled", but a teal cross beside a green
  // tick reads as two kinds of success. `orange` is the only unambiguous warning tone in the palette.
  const tone = deliveredAt
    ? { bg: C.greenLight, fg: C.green, label: "In the kitchen" }
    : { bg: C.orangeLight, fg: C.orange, label: "Not in the kitchen" };

  return (
    <Card>
      <SectionTitle>Kitchen</SectionTitle>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
          background: tone.bg, color: tone.fg,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone.fg }} />
          {tone.label}
        </span>
        {deliveredAt ? (
          <span style={{ fontSize: 12, color: C.textMed }}>
            {new Date(deliveredAt).toLocaleString()}
          </span>
        ) : null}
      </div>

      {nothingReported ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: C.orange }}>
          The screen never reported a delivery attempt for this order.
        </div>
      ) : null}

      {unrouted > 0 ? (
        // The quiet failure: no station claimed these lines, so nobody was ever asked to make them.
        // It reads as a normal order everywhere else, which is exactly why it needs saying here.
        <div style={{
          background: C.orangeLight, border: `1px solid ${C.orange}33`,
          borderLeft: `4px solid ${C.orange}`, borderRadius: 8,
          padding: "10px 14px", marginBottom: stations.length ? 14 : 0,
          fontSize: 13, fontWeight: 600, color: C.text,
        }}>
          {unrouted} {unrouted === 1 ? "line" : "lines"} reached no kitchen — no station is set up to
          make {unrouted === 1 ? "it" : "them"}. Check the station's categories on the POS.
        </div>
      ) : null}

      {stations.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {stations.map((s, i) => (
            <div
              key={`${s.stationId ?? "x"}-${i}`}
              style={{
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 8, padding: "8px 12px",
              }}>
              {s.ok
                ? <CheckCircle size={15} color={C.green} />
                : <XCircle size={15} color={C.orange} />}
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {/* Older records carry the id only — it was resolved to a name at write time from
                    the day this section was added, so a fallback is still needed. */}
                {s.stationName || (s.stationId === 0 ? "Web KDS" : `Station ${s.stationId ?? "--"}`)}
              </span>
              {!s.ok && s.error ? (
                <span style={{ fontSize: 12, color: C.orange }}>{s.error}</span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {kitchen?.cancelRequestedAt ? (
        // A kitchen asking to cancel a PAID order is never applied automatically — it would close the
        // order and keep the customer's money. It is surfaced here so staff can settle it in person.
        <div style={{
          background: C.orangeLight, border: `1px solid ${C.orange}33`,
          borderLeft: `4px solid ${C.orange}`, borderRadius: 8,
          padding: "10px 14px", marginTop: 14,
          fontSize: 13, fontWeight: 600, color: C.text,
        }}>
          The kitchen asked to cancel this order
          {kitchen.cancelRequestedBy ? ` from ${kitchen.cancelRequestedBy}` : ""} on{" "}
          {new Date(kitchen.cancelRequestedAt).toLocaleString()}. It was left open — a paid order is
          not cancelled automatically.
        </div>
      ) : null}

    </Card>
  );
};

const money = (v) => `SAR ${Number(v ?? 0).toFixed(2)}`;

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * A line's gross subtotal — product AND modifiers, before its discount.
 *
 * `price * quantity` alone was wrong: `lineTotal` includes modifier money, so on any line
 * with add-ons the row's own Subtotal − Discount did not reach its Line Total and the table
 * looked like it had lost the difference.
 */
const lineGrossSubtotal = (item) => {
  const base = (Number(item?.price) || 0) * (Number(item?.quantity) || 1);
  const modifiers = (item?.modifiers || []).reduce(
    (sum, m) => sum + (Number(m?.price) || 0) * (Number(m?.quantity) || 1),
    0
  );
  return round2(base + modifiers);
};

/**
 * A line's discount in GROSS, to match the gross columns beside it.
 *
 * The cell used to print `lineDiscountNet`, a NET figure in a row of gross ones — and on a
 * reward line it printed 0.00, because the reward is deliberately excluded from that column.
 * The tags carry the truth: `originalAmount` is gross on entries written from 20260914, and
 * reward tags older than that put their gross in `amount`.
 */
const lineGrossDiscount = (item) =>
  round2((item?.discounts || []).reduce(
    (sum, d) => sum + (Number(d?.originalAmount ?? d?.amount) || 0),
    0
  ));

/** Named discount rows, gross, from the server's one breakdown. */
const breakdownRows = (orderData) => orderData?.totalsBreakdown?.gross?.rows ?? [];

/**
 * The reward/voucher half of the net column.
 *
 * Prefers the stored column, and falls back to the identity the order was assembled with —
 * `discountNet = productDiscountNet + orderDiscountNet + rewardDiscountNet` — which is exact
 * and is the only way to get the figure for rows written before the column existed.
 */
const rewardNet = (orderData) => {
  if (orderData?.rewardDiscountNet != null) return Number(orderData.rewardDiscountNet);
  return round2(
    (Number(orderData?.discountNet) || 0)
    - (Number(orderData?.productDiscountNet) || 0)
    - (Number(orderData?.orderDiscountNet) || 0)
  );
};

const METHOD_LABEL = {
  wallet: "Wallet balance",
  card: "Card",
  applepay: "Apple Pay",
  stcpay: "STC Pay",
  cash: "Cash",
  points: "Loyalty points",
  other: "Other",
};

const TenderBreakdown = ({ orderData }) => {
  const rows = Array.isArray(orderData?.orderPayments) ? orderData.orderPayments : [];

  if (!rows.length) {
    return (
      <div style={{ padding: "8px 0", fontSize: 13, color: C.textMed }}>
        <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>
          No tender breakdown recorded
        </div>
        Expected for orders placed before the wallet launched, third-party and waiter orders, unpaid
        orders, and fully reward-redeemed orders. The POS falls back to a single payment line for
        these, exactly as before.
      </div>
    );
  }

  const sum = rows.reduce((acc, r) => acc + Number(r?.amount ?? 0), 0);
  const total = Number(orderData?.grandTotal ?? 0);
  // The same 0.01 epsilon and the same invariant the nightly reconcile job asserts. Surfacing it
  // here means support sees a broken tender set while looking at the order rather than waiting for
  // the drift email — and a set that does not add up is precisely what would corrupt Odoo.
  const mismatch = Math.abs(sum - total) > 0.01;

  const detailFor = (r) => {
    if (r?.method === "wallet") {
      const promo = Number(r?.meta?.promotional ?? 0);
      const purchased = Number(r?.meta?.purchased ?? 0);
      // The bucket split matters on a refund: promotional credit returns to promotional and
      // purchased to purchased, because only purchased is transferable and refundable.
      return promo || purchased
        ? `promotional ${promo.toFixed(2)} · purchased ${purchased.toFixed(2)}`
        : "--";
    }
    return [r?.meta?.sourceType, r?.providerRef].filter(Boolean).join(" · ") || "--";
  };

  return (
    <div>
      {mismatch && (
        <div style={{
          marginBottom: 14,
          padding: "12px 16px",
          borderRadius: 10,
          background: C.orangeLight,
          border: `1px solid ${C.orange}`,
          fontSize: 13,
        }}>
          <div style={{ fontWeight: 700, color: C.orange, marginBottom: 4 }}>
            Tenders do not reconcile
          </div>
          <div style={{ color: C.text, fontSize: 12 }}>
            Recorded payments total <strong>{money(sum)}</strong> against a grand total of{" "}
            <strong>{money(total)}</strong>. The nightly wallet reconciliation reports this order.
            Do not correct balances by hand — use the audited wallet adjustment tools.
          </div>
        </div>
      )}

      <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
        <table style={tbl.table}>
          <thead>
            <tr>
              <th style={tbl.th}>Method</th>
              <th style={tbl.th}>Details</th>
              <th style={tbl.th}>Odoo reference</th>
              <th style={tbl.th}>Status</th>
              <th style={{ ...tbl.th, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r?.id ?? idx} className="rpt-row">
                <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>
                  {METHOD_LABEL[r?.method] || r?.method || "--"}
                </td>
                <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{detailFor(r)}</td>
                <td style={{ ...tbl.td, color: C.textLight, fontSize: 12 }}>
                  {r?.posPaymentMethodRef || "--"}
                </td>
                <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{r?.status || "--"}</td>
                <td style={{
                  ...tbl.td,
                  textAlign: "right",
                  fontWeight: 700,
                  color: r?.method === "wallet" ? C.green : C.text,
                }}>
                  {money(r?.amount)}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: `2px solid ${C.border}` }}>
              <td style={{ ...tbl.td, color: C.text, fontWeight: 700 }} colSpan={4}>
                Tendered
              </td>
              <td style={{
                ...tbl.td,
                textAlign: "right",
                fontWeight: 800,
                fontSize: 14,
                color: mismatch ? C.orange : C.coral,
              }}>
                {money(sum)}
              </td>
            </tr>
            <tr>
              <td style={{ ...tbl.td, color: C.textMed }} colSpan={4}>
                Grand total
              </td>
              <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>
                {money(total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Membership and loyalty, side by side, exactly as this order froze them.
 *
 * Both snapshots have been written on every order for a while and read by nothing, so the
 * question "what terms was this order actually charged under" had no answer in the panel.
 * They are shown together because that is how the question arrives — a customer asks why
 * they got a discount but no points, and the answer needs both halves.
 *
 * Everything here comes from the ORDER, never from the tier's current row. An admin who
 * edits a tier tomorrow must not change what a receipt from today says.
 */
const ProgrammesSection = ({ orderData }) => {
  const membership = orderData?.membershipSnapshot || null;
  const loyalty = orderData?.loyaltySnapshot || null;
  const skipReason = orderData?.loyaltyEarnSkipReason || null;

  // Nothing to say at all — a plain order on a merchant running neither programme.
  if (!membership && !loyalty && !skipReason) return null;

  const row = (label, value) => (
    <tr>
      <td style={{ padding: "6px 0", color: C.textMed, width: "50%" }}>{label}</td>
      <td style={{ padding: "6px 0", color: C.text, fontWeight: 500 }}>{value}</td>
    </tr>
  );

  return (
    <div style={{ marginTop: 20 }}>
      <SectionTitle>Programmes</SectionTitle>
      <Row>
        <Col md="6">
          <div style={{ padding: "14px 16px", borderRadius: 10, border: `1px solid ${C.border}`, height: "100%" }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: C.text }}>
              Membership
            </div>
            {!membership ? (
              <div style={{ fontSize: 12, color: C.textLight }}>
                No membership discount on this order.
              </div>
            ) : (
              <table style={{ width: "100%", fontSize: 13 }}>
                <tbody>
                  {row("Card", <span style={{ fontFamily: "monospace" }}>{membership.membershipRef || "--"}</span>)}
                  {row("Tier", membership.typeName || "--")}
                  {row("Terms", formatDiscount(membership.discountType, membership.discountValue))}
                  {row("Discount given", <span style={{ color: C.coral }}>− {money(orderData?.membershipDiscount)}</span>)}
                  {row("Qualifying subtotal", money(membership.eligibleSubtotal))}
                  {row("Modifiers included", membership.applyToModifiers ? "Yes" : "No")}
                  {row("Combined with a coupon", membership.combinableWithCoupons ? "Allowed" : "Not allowed")}
                  {row("Loyalty for members", LOYALTY_POLICY_LABELS[membership.memberLoyaltyPolicy] || "Default")}
                  {Array.isArray(membership.ineligibleProductIds) && membership.ineligibleProductIds.length > 0
                    && row("Items not covered", `${membership.ineligibleProductIds.length} product(s)`)}
                  {membership.capsAtApply && row(
                    "Limits at the time",
                    <span style={{ fontSize: 12, color: C.textMed }}>
                      {[
                        `${membership.capsAtApply.usedDailyOrders ?? 0} of ${formatCap(membership.capsAtApply.maxDailyOrders)} today`,
                        `${membership.capsAtApply.usedMonthlyOrders ?? 0} of ${formatCap(membership.capsAtApply.maxMonthlyOrders)} this month`,
                      ].join(" · ")}
                    </span>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Col>

        <Col md="6">
          <div style={{ padding: "14px 16px", borderRadius: 10, border: `1px solid ${C.border}`, height: "100%" }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: C.text }}>
              Loyalty
            </div>
            {!loyalty ? (
              <div style={{ fontSize: 12, color: C.textLight }}>
                No loyalty programme on this order.
              </div>
            ) : (
              <table style={{ width: "100%", fontSize: 13 }}>
                <tbody>
                  {row("Programme", loyalty.name || "--")}
                  {row("Type", loyalty.loyaltyType === "stamps" ? "Stamps" : "Points")}
                  {/*
                    Read off the order's FROZEN snapshot, so this row has to speak both
                    conventions: orders placed before the change carry only basePercentage,
                    and stating one as if it were the other would be out by a factor of 100.
                    Shown in the unit it was actually configured in, never converted.
                  */}
                  {loyalty.loyaltyType !== "stamps" && row(
                    "Earn rate",
                    loyalty.basePointsPerSar != null
                      ? `${loyalty.basePointsPerSar} points / SAR`
                      : loyalty.basePercentage != null
                        ? `${loyalty.basePercentage}% of spend`
                        : "--"
                  )}
                  {row("Earning base", money(orderData?.eligibleSubtotalForLoyalty))}
                  {loyalty.eligibleLoyaltyAmount
                    ? row("Programme minimum", money(loyalty.eligibleLoyaltyAmount))
                    : null}
                </tbody>
              </table>
            )}
            {/*
              The one thing that makes a null loyalty result legible. Without it, "no points"
              could mean no programme, an ineligible basket, or a membership order — and
              which of the three it is, is the whole of the support question.
            */}
            {skipReason && (
              <div style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: C.orangeLight, fontSize: 12, color: C.text,
              }}>
                <strong>No points earned.</strong>{" "}
                {LOYALTY_SKIP_LABELS[skipReason] || BLOCK_REASON_LABELS[skipReason] || skipReason}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

const OrderDetailModal = ({
  selectedOrder,
  setOrderModalOpen,
  orderModalOpen,
}) => {
  const [orderData, setOrderData] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [availability, setAvailability] = useState(null);
  // Lets the "Linked Orders" tab navigate between a main order and its add-ons
  // within this same modal, without touching the parent's selectedOrder.
  const [viewOrderId, setViewOrderId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const idToFetch = viewOrderId ?? selectedOrder?.id;
      if (!idToFetch) return;
      setIsLoading(true);
      const orderDetails = await getOrderById(idToFetch);
      setOrderData(orderDetails);
      const transformData = orderDetails?.OrderItems?.map((item) => ({
        ...item.Product,
        modifiers: item.modifiers,
        orderId: item.OrderId,
        ProductId: item.ProductId,
        createdAt: item.createdAt,
        quantity: item?.quantity,
        price: item?.price,
        note: item?.note,
        discounts: item?.discounts,
        // Carried so the row can say WHY it costs nothing. Without it a redeemed line is
        // just a 0.00 with no explanation anywhere on the page.
        isRewardRedemption: item?.isRewardRedemption,
        lineSubtotalNet: item?.lineSubtotalNet,
        lineDiscountNet: item?.lineDiscountNet,
        lineTax: item?.lineTax,
        lineTotal: item?.lineTotal,
      }));
      setOrderItems(transformData || []);
      setIsLoading(false);
    };
    fetchData();
  }, [viewOrderId, selectedOrder?.id]);

  const fetchProducts = async () => {
    setIsFetching(true);
    setAvailability(null);
    const branchId = selectedOrder?.Branch?.id;
    const productIds = orderItems.map((item) => item.ProductId);
    if (!branchId || productIds.length === 0) {
      setIsFetching(false);
      return;
    }
    const resData = await checkUnavailableProducts(branchId, productIds);
    if (resData && Array.isArray(resData.unavailableProductIds)) {
      setAvailability({ checked: true, unavailableIds: resData.unavailableProductIds });
    }
    setIsFetching(false);
  };

  const toggle = (tab) => { if (activeTab !== tab) setActiveTab(tab); };

  const handleClose = () => {
    setOrderModalOpen(false);
    setAvailability(null);
    setActiveTab("1");
    setViewOrderId(null);
  };

  const openLinkedOrder = (id) => {
    if (!id) return;
    setViewOrderId(id);
    setActiveTab("1");
  };

  const hasLinkedOrders = !!orderData?.parentOrderId || (orderData?.childOrders?.length > 0);

  const tabLink = (id, label) => (
    <NavItem>
      <NavLink
        className={activeTab === id ? "active" : ""}
        onClick={() => toggle(id)}
        style={{
          cursor: "pointer",
          color: activeTab === id ? C.coral : C.textMed,
          fontWeight: 700, fontSize: 13, letterSpacing: 0.3,
          border: "none",
          borderBottom: activeTab === id ? `2px solid ${C.coral}` : "2px solid transparent",
          background: "transparent",
          padding: "10px 16px",
        }}
      >
        {label}
      </NavLink>
    </NavItem>
  );

  return (
    <Modal isOpen={orderModalOpen} toggle={handleClose} style={{ minWidth: '85%' }}>
      <style>{reportingGlobalCSS}</style>

      <ModalHeader
        toggle={handleClose}
        style={{ borderBottom: `1px solid ${C.border}`, background: C.white }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 700, color: C.text, fontSize: 16 }}>Order Details</span>
          {orderData?.id && (
            <span style={{
              fontSize: 12, fontWeight: 700, color: C.textMed,
              padding: "2px 10px", borderRadius: 6, background: C.bg,
              border: `1px solid ${C.border}`,
            }}>#{orderData?.id}</span>
          )}
          <StatusPill status={orderData?.status} />
        </div>
      </ModalHeader>

      <ModalBody style={{ background: C.bg, padding: 20 }} className="rpt-wrap">
        {isLoading || !orderData ? (
          <RLoader />
        ) : (
          <>
            {orderData?.status === "canceled" && <CancellationBanner orderData={orderData} />}

            {/* Identifiers */}
            <Card>
              <SectionTitle>Identifiers</SectionTitle>
              <Row className="g-3">
                <Col md="3"><InfoCell label="Order ID" value={orderData?.id} /></Col>
                <Col md="3"><InfoCell label="Channel Order ID" value={orderData?.orderInformation?.channelOrderId || orderData?.channelOrderId || "--"} /></Col>
                <Col md="3"><InfoCell label="Merchant Order ID" value={orderData?.merchantOrderId || "--"} /></Col>
                <Col md="3"><InfoCell label="External ID" value={orderData?.externalId || "--"} /></Col>
                <Col md="3"><InfoCell label="Date & Time" value={orderData?.createdAt ? new Date(orderData.createdAt).toLocaleString() : "--"} /></Col>
                {/* Hubs (Deliverect, Blend) resolve the delivery app's name, which
                    is far more useful than the raw channel id on its own. */}
                <Col md="3"><InfoCell label="Channel" value={
                  orderData?.orderInformation?.channelName
                    ? `${orderData.orderInformation.channelName}${orderData?.orderInformation?.channelId ? ` (${orderData.orderInformation.channelId})` : ""}`
                    : orderData?.orderInformation?.channelId || "--"
                } /></Col>
                <Col md="3"><InfoCell label="Order Type" value={orderData?.orderType || "--"} /></Col>
                <Col md="3"><InfoCell label="Order Nature" value={orderData?.orderNature || "--"} /></Col>
                {(orderData?.App?.name || orderData?.App?.reference) && (
                  <Col md="3"><InfoCell label="App" value={orderData.App.name || orderData.App.reference} /></Col>
                )}
                {orderData?.appVersion && (
                  <Col md="3"><InfoCell label="App Version" value={orderData.appVersion} /></Col>
                )}
                {orderData?.platform && (
                  <Col md="3"><InfoCell label="Platform" value={orderData.platform} /></Col>
                )}
                {orderData?.orderInformation?.orderNote && String(orderData.orderInformation.orderNote).trim() && (
                  <Col md="12"><InfoCell label="Order Note" value={orderData.orderInformation.orderNote} /></Col>
                )}
              </Row>
            </Card>

            {/* Operations */}
            <Card>
              <SectionTitle>Operations</SectionTitle>
              <Row className="g-3">
                <Col md="3"><InfoCell label="Branch" value={orderData?.Branch?.name || "--"} /></Col>
                <Col md="3"><InfoCell label="Payment" value={orderData?.payment || "--"} /></Col>
                <Col md="3"><InfoCell label="Scheduled" value={orderData?.isScheduled ? "Yes" : "No"} /></Col>
                {/* `sentToMerchant` is only written when a till acknowledges the order, so this
                    label is now literally true. It was not before: the server used to set the flag
                    the moment it emitted over the socket, which reported a confident "Yes" for
                    orders no till ever saw and sent people hunting for the fault on the POS. */}
                <Col md="3"><InfoCell label="Received By POS" value={orderData?.sentToMerchant ? "Yes" : "No"} /></Col>
                <Col md="3"><InfoCell label="Received At" value={orderData?.sentToMerchantAt ? new Date(orderData.sentToMerchantAt).toLocaleString() : "--"} /></Col>
                <Col md="3"><InfoCell label="Resend Requested" value={orderData?.resendRequestedAt ? new Date(orderData.resendRequestedAt).toLocaleString() : "--"} /></Col>
                <Col md="3"><InfoCell label="Last Retry" value={orderData?.lastRetryAt ? new Date(orderData.lastRetryAt).toLocaleString() : "--"} /></Col>
                {/* Stored since the retry mechanism was written and never shown until now: two
                    retries is the cap, so this says whether recovery still had attempts left. */}
                <Col md="3"><InfoCell label="Delivery Retries" value={orderData?.retryCount ?? 0} /></Col>
                {orderData?.table && (
                  <>
                    <Col md="3">
                      <InfoCell
                        label="Table"
                        value={
                          orderData.table.name
                            ? `${orderData.table.name}${orderData.table.tableNumber != null ? ` (#${orderData.table.tableNumber})` : ""}`
                            : orderData.table.tableNumber != null
                              ? `#${orderData.table.tableNumber}`
                              : "--"
                        }
                      />
                    </Col>
                    <Col md="3"><InfoCell label="Floor" value={orderData.table.Floor?.name || "--"} /></Col>
                    <Col md="3"><InfoCell label="Seats" value={orderData.table.seatingCapacity ?? "--"} /></Col>
                  </>
                )}
              </Row>
            </Card>

            {/* Kitchen — kiosk orders only; renders nothing for every other order nature. */}
            <KitchenDelivery orderData={orderData} />

            {/* Customer */}
            <Card>
              <SectionTitle>Customer</SectionTitle>
              <Row className="g-3">
                <Col md="3"><InfoCell label="ID" value={orderData?.User?.id || "--"} /></Col>
                <Col md="4"><InfoCell label="Name" value={`${orderData?.User?.firstName || ""} ${orderData?.User?.lastName || ""}`.trim() || "--"} /></Col>
                <Col md="3"><InfoCell label="Phone" value={orderData?.User?.phoneNo || "--"} /></Col>
                {orderData?.User?.appName ? <Col md="2"><InfoCell label="App" value={orderData?.User?.appName || "--"} /></Col> : null}
                {orderData?.orderInformation?.carInformation ? (
                  <Col md="12"><InfoCell label="Car Information" value={orderData.orderInformation.carInformation} /></Col>
                ) : null}
              </Row>
            </Card>

            {orderData?.orderInformation?.orderNote ? (
              <Card>
                <SectionTitle>Order Note</SectionTitle>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: C.text,
                  background: C.bg, border: `1px solid ${C.border}`,
                  padding: "10px 12px", borderRadius: 8,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {orderData.orderInformation.orderNote}
                </div>
              </Card>
            ) : null}

            {/* Tabs */}
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "0 12px", borderBottom: `1px solid ${C.border}`,
                background: "linear-gradient(180deg, #FFFFFF, #FAFBFC)",
              }}>
                <Nav style={{ borderBottom: "none", flexWrap: "nowrap" }}>
                  {tabLink("1", "Products")}
                  {tabLink("2", "Status Updates")}
                  {tabLink("3", "Feedback")}
                  {tabLink("4", "Refund Details")}
                  {tabLink("7", "Payment Breakdown")}
                  {tabLink("5", "Order Payload")}
                  {hasLinkedOrders ? tabLink("6", "Linked Orders") : null}
                </Nav>
                {activeTab === "1" && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={fetchProducts}
                    disabled={isFetching}
                    style={{ fontSize: 12, fontWeight: 700, borderRadius: 8 }}
                  >
                    {isFetching ? "Checking..." : "Check Availability"}
                  </Button>
                )}
              </div>

              <TabContent activeTab={activeTab} style={{ padding: 16 }}>
                {/* Products */}
                <TabPane tabId="1">
                  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>#</th>
                          <th style={tbl.th}>Product</th>
                          <th style={tbl.th}>Name</th>
                          <th style={{ ...tbl.th, textAlign: "right" }}>Price</th>
                          <th style={{ ...tbl.th, textAlign: "center" }}>Qty</th>
                          <th style={{ ...tbl.th, textAlign: "right" }}>Subtotal</th>
                          <th style={{ ...tbl.th, textAlign: "right" }}>Discount</th>
                          <th style={{ ...tbl.th, textAlign: "right" }}>Line Total</th>
                          {availability?.checked && <th style={{ ...tbl.th, textAlign: "center" }}>Availability</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems?.map((item, i) => (
                          <React.Fragment key={i}>
                            <tr className="rpt-row">
                              <td style={{ ...tbl.td, color: C.textLight, fontWeight: 700 }}>{i + 1}</td>
                              <td style={{ ...tbl.td, width: 80 }}>
                                <img
                                  src={process.env.REACT_APP_URL + item.image1 || "https://www.plas-pak.com/wp-content/themes/plaspak/img/product-placeholder.gif"}
                                  onError={(e) => { e.target.src = "https://www.plas-pak.com/wp-content/themes/plaspak/img/product-placeholder.gif"; }}
                                  alt={item.title}
                                  style={{ height: 40, width: 60, objectFit: "cover", borderRadius: 6 }}
                                />
                              </td>
                              <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>
                                {item.title}
                                {item?.isRewardRedemption && (
                                  <span style={{
                                    marginLeft: 8,
                                    padding: "2px 8px",
                                    borderRadius: 999,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                    color: C.green,
                                    background: C.greenLight || "#E8F7F1",
                                    border: `1px solid ${C.green}`,
                                  }}>
                                    {item.discounts?.some((d) => d?.reference) ? "Voucher" : "Redeemed"}
                                  </span>
                                )}
                              </td>
                              <td style={{ ...tbl.td, textAlign: "right" }}>{money(item.price)}</td>
                              <td style={{ ...tbl.td, textAlign: "center" }}>{item.quantity || 1}</td>
                              <td style={{ ...tbl.td, textAlign: "right" }}>{money(lineGrossSubtotal(item))}</td>
                              <td style={{ ...tbl.td, textAlign: "right", color: Array.isArray(item?.discounts) && item.discounts.length > 0 ? C.coral : C.textLight }}>
                                {Array.isArray(item?.discounts) && item.discounts.length > 0 ? `− ${money(lineGrossDiscount(item))}` : "--"}
                              </td>
                              <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, color: C.text }}>
                                {item?.lineTotal != null ? money(item.lineTotal) : "--"}
                              </td>
                              {availability?.checked && (
                                <td style={{ ...tbl.td, textAlign: "center" }}>
                                  {!availability.unavailableIds.includes(item.ProductId)
                                    ? <CheckCircle color={C.green} size={18} />
                                    : <XCircle color={C.coral} size={18} />}
                                </td>
                              )}
                            </tr>
                            {item?.note && String(item.note).trim() && (
                              <tr style={{ background: C.borderLight }}>
                                <td style={tbl.td}></td>
                                <td style={{ ...tbl.td, color: C.textMed, fontStyle: "italic", fontSize: 12 }} colSpan={availability?.checked ? 8 : 7}>
                                  📝 {item.note}
                                </td>
                              </tr>
                            )}
                            {item.modifiers?.length > 0 && item.modifiers.map((mod, modIdx) => (
                              <tr key={`${i}-m-${modIdx}`} style={{ background: C.borderLight }}>
                                <td style={tbl.td}></td>
                                <td style={{ ...tbl.td, color: C.textMed, fontStyle: "italic", fontSize: 12 }} colSpan={2}>
                                  + {mod.name}
                                </td>
                                <td style={{ ...tbl.td, textAlign: "right", fontSize: 12, color: C.textMed }}>{money(mod.price)}</td>
                                <td style={{ ...tbl.td, textAlign: "center", fontSize: 12, color: C.textMed }}>{mod.quantity}</td>
                                <td style={{ ...tbl.td, textAlign: "right", fontSize: 12, color: C.textMed }}>{money(mod.price * mod.quantity)}</td>
                                <td style={tbl.td} colSpan={availability?.checked ? 3 : 2}></td>
                              </tr>
                            ))}
                            {Array.isArray(item?.discounts) && item.discounts.length > 0 && item.discounts.map((disc, dIdx) => (
                              <tr key={`${i}-d-${dIdx}`} style={{ background: "#FFF8F8" }}>
                                <td style={tbl.td}></td>
                                <td style={{ ...tbl.td, color: C.coral, fontSize: 12, fontWeight: 600 }} colSpan={2}>
                                  ↳ Discount: {disc?.name || disc?.type}
                                </td>
                                <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>{disc?.type}</td>
                                <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }} colSpan={2}>{disc?.reference || "--"}</td>
                                <td style={{ ...tbl.td, textAlign: "right", fontSize: 12, color: C.textMed }}>− {money(disc?.originalAmount)}</td>
                                <td style={{ ...tbl.td, textAlign: "right", fontSize: 12, color: C.coral, fontWeight: 700 }}>− {money(disc?.amount)}</td>
                                {availability?.checked && <td style={tbl.td}></td>}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabPane>

                {/* Status Updates */}
                <TabPane tabId="2">
                  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>Previous</th>
                          <th style={tbl.th}>New</th>
                          <th style={tbl.th}>Changed By</th>
                          <th style={tbl.th}>Reason</th>
                          <th style={tbl.th}>Updated At</th>
                          <th style={tbl.th}>Device ID</th>
                          <th style={tbl.th}>IP</th>
                          <th style={tbl.th}>Aggregator Response</th>
                          {orderData?.Merchant?.enable_smart_order_tracking_takzilla ? (
                            <th style={tbl.th}>Tracking</th>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {orderData?.OrderStatusHistories?.length ? orderData.OrderStatusHistories.map((history) => (
                          <tr key={history?.id} className="rpt-row">
                            <td style={tbl.td}><StatusPill status={history?.previousStatus} /></td>
                            <td style={tbl.td}><StatusPill status={history?.newStatus} /></td>
                            <td style={{ ...tbl.td, minWidth: 190 }}><ChangedByCell history={history} /></td>
                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, maxWidth: 280, whiteSpace: "normal" }}>
                              {describeStatusChange(history).reason || "--"}
                            </td>
                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{history?.createdAt ? new Date(history?.createdAt).toLocaleString() : "--"}</td>
                            <td style={{ ...tbl.td, color: C.textMed }}>{history?.metadata?.deviceID || "--"}</td>
                            <td style={{ ...tbl.td, color: C.textMed }}>{history?.metadata?.ip || "--"}</td>
                            <td style={{ ...tbl.td, maxWidth: 300 }}>
                              <JsonBlock value={history?.aggregatorResponse} empty="--" />
                            </td>
                            {orderData?.Merchant?.enable_smart_order_tracking_takzilla ? (
                              <td style={{ ...tbl.td, maxWidth: 300 }}>
                                <JsonBlock value={history?.trackingSurveillance} empty="--" />
                              </td>
                            ) : null}
                          </tr>
                        )) : (
                          <tr>
                            <td style={{ ...tbl.td, textAlign: "center", color: C.textLight }} colSpan={orderData?.Merchant?.enable_smart_order_tracking_takzilla ? 9 : 8}>No status updates</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </TabPane>

                {/* Feedback */}
                <TabPane tabId="3">
                  {orderData?.feedback ? (
                    <Row className="g-3">
                      <Col md="2">
                        <img
                          alt=""
                          src={orderData?.feedback?.image
                            ? process.env.REACT_APP_URL + orderData?.feedback.image
                            : "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png"}
                          onError={(e) => { e.target.src = "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png"; }}
                          style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover", border: `1px solid ${C.border}` }}
                        />
                      </Col>
                      <Col md="10">
                        <Row className="g-3">
                          <Col md="4"><InfoCell label="Customer ID" value={orderData?.feedback?.userId} /></Col>
                          <Col md="4"><InfoCell label="Customer Name" value={orderData?.feedback?.customerName} /></Col>
                          <Col md="4"><InfoCell label="Customer Phone" value={orderData?.feedback?.customerPhone} /></Col>
                          <Col md="4"><InfoCell label="Date" value={orderData?.feedback?.createdAt ? new Date(orderData.feedback.createdAt).toLocaleString() : "--"} /></Col>
                          <Col md="8"><InfoCell label="Subject" value={orderData?.feedback?.subject?.title || "--"} /></Col>
                          <Col md="12"><InfoCell label="Description" value={orderData?.feedback?.description || "--"} /></Col>
                        </Row>
                      </Col>
                    </Row>
                  ) : (
                    <div style={{ color: C.textMed, fontSize: 13, padding: "8px 0" }}>No feedback submitted.</div>
                  )}
                </TabPane>

                {/* Refund Details */}
                <TabPane tabId="4">
                  <JsonBlock value={orderData?.paymentRefundInfo} empty="No refund details available." />
                </TabPane>

                {/* Payment Breakdown — how the order was actually tendered */}
                <TabPane tabId="7">
                  <TenderBreakdown orderData={orderData} />
                </TabPane>

                {/* Order Payload */}
                <TabPane tabId="5">
                  <JsonBlock value={orderData?.orderPayload} empty="No order payload stored." />
                </TabPane>

                {/* Linked Orders — main ↔ subsequent (add-on) menu orders for the same table */}
                <TabPane tabId="6">
                  {orderData?.parentOrderId ? (
                    <div style={{ marginBottom: 16 }}>
                      <SectionTitle>Main Order</SectionTitle>
                      <div
                        onClick={() => openLinkedOrder(orderData?.parentOrderId)}
                        style={{
                          cursor: "pointer", padding: "10px 14px", borderRadius: 10,
                          border: `1px solid ${C.border}`, display: "flex",
                          alignItems: "center", justifyContent: "space-between", gap: 12,
                        }}
                      >
                        <span style={{ fontWeight: 700, color: C.coral }}>Order #{orderData?.parentOrderId}</span>
                        {orderData?.parentOrder ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <StatusPill status={orderData?.parentOrder?.status} />
                            <span style={{ color: C.textMed, fontSize: 13 }}>{money(orderData?.parentOrder?.grandTotal ?? orderData?.parentOrder?.productCost)}</span>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {orderData?.childOrders?.length > 0 ? (
                    <div>
                      <SectionTitle>Subsequent Orders ({orderData?.childOrders?.length})</SectionTitle>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {orderData?.childOrders?.map((child) => (
                          <div
                            key={child?.id}
                            onClick={() => openLinkedOrder(child?.id)}
                            style={{
                              cursor: "pointer", padding: "10px 14px", borderRadius: 10,
                              border: `1px solid ${C.border}`, display: "flex",
                              alignItems: "center", justifyContent: "space-between", gap: 12,
                            }}
                          >
                            <span style={{ fontWeight: 700, color: C.coral }}>Order #{child?.id}</span>
                            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <StatusPill status={child?.status} />
                              <span style={{ color: C.textMed, fontSize: 13 }}>{money(child?.grandTotal ?? child?.productCost)}</span>
                              <span style={{ color: C.textLight, fontSize: 12 }}>{child?.createdAt ? new Date(child.createdAt).toLocaleString() : ""}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {!orderData?.parentOrderId && !(orderData?.childOrders?.length > 0) ? (
                    <div style={{ color: C.textMed, fontSize: 13 }}>No linked orders.</div>
                  ) : null}
                </TabPane>
              </TabContent>
            </Card>

            {/* Financial Breakdown — at bottom */}
            {orderData?.grandTotal != null && (
              <Card>
                <SectionTitle>Financial Breakdown</SectionTitle>
                {orderData?.hasAmountConflict && (() => {
                  const partnerAmount = orderData?.orderInformation?.orderFinalAmount;
                  const adj = (Array.isArray(orderData?.orderDiscounts) ? orderData.orderDiscounts : [])
                    .find((d) => d?.type === "adjustment");
                  const adjGross = Number(adj?.originalAmount ?? 0);
                  const direction = adjGross > 0 ? "Customer charged less" : adjGross < 0 ? "Customer charged more" : "";
                  return (
                    <div style={{
                      marginBottom: 16,
                      padding: "12px 16px",
                      borderRadius: 10,
                      background: C.coralLight || "#FFF1ED",
                      border: `1px solid ${C.coral || "#FF5A36"}`,
                      fontSize: 13,
                    }}>
                      <div style={{ fontWeight: 700, color: C.coral || "#FF5A36", marginBottom: 4 }}>
                        Amount Reconciliation Applied
                      </div>
                      <div style={{ color: C.text, fontSize: 12 }}>
                        Partner orderFinalAmount: <strong>{money(partnerAmount)}</strong>
                        {" · "}
                        Adjustment: <strong>{adjGross >= 0 ? "−" : "+"} {money(Math.abs(adjGross))}</strong>
                        {direction && <> {" · "} <em>{direction}</em></>}
                      </div>
                    </div>
                  );
                })()}
                <Row className="g-3">
                  <Col md="6">
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Gross (customer-facing)</div>
                    <table style={{ width: "100%", fontSize: 13 }}>
                      <tbody>
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>Subtotal</td><td style={{ padding: "8px 0", textAlign: "right", color: C.text }}>{money(orderData?.productCost)}</td></tr>
                        {/* One NAMED row per discount, so the column explains itself. It used
                            to list the product and order halves only — and a reward lives in
                            neither, so a redeemed order's rows never reached its total and
                            nothing on the page said where the money went. These rows are
                            guaranteed by the server to sum to `discount`. */}
                        {breakdownRows(orderData).map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: "8px 0", color: C.textMed }}>
                              {row.label}
                              {row.reference && <span style={{ color: C.textLight, fontSize: 12 }}> ({row.reference})</span>}
                            </td>
                            <td style={{ padding: "8px 0", textAlign: "right", color: C.coral }}>
                              {row.amount < 0 ? "+" : "−"} {money(Math.abs(row.amount))}
                            </td>
                          </tr>
                        ))}
                        <tr><td style={{ padding: "8px 0", color: C.textLight }}>Total discount</td><td style={{ padding: "8px 0", textAlign: "right", color: C.textLight }}>− {money(orderData?.discount)}</td></tr>
                        <tr style={{ borderTop: `2px solid ${C.border}` }}><td style={{ padding: "10px 0", color: C.text, fontWeight: 700 }}>Total</td><td style={{ padding: "10px 0", textAlign: "right", color: C.coral, fontWeight: 800, fontSize: 15 }}>{money(orderData?.grandTotal)}</td></tr>
                        {/* The VAT actually charged, stated as a memo under the total rather
                            than as an addend — it is already inside every figure above. */}
                        <tr><td style={{ padding: "4px 0", color: C.textLight, fontSize: 12 }}>includes {orderData?.totalsBreakdown?.taxName || "VAT"} ({orderData?.totalsBreakdown?.taxPercentage ?? 15}%)</td><td style={{ padding: "4px 0", textAlign: "right", color: C.textLight, fontSize: 12 }}>{money(orderData?.taxAmount)}</td></tr>
                      </tbody>
                    </table>
                  </Col>
                  <Col md="6">
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Net (accounting)</div>
                    <table style={{ width: "100%", fontSize: 13 }}>
                      <tbody>
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>Subtotal</td><td style={{ padding: "8px 0", textAlign: "right", color: C.text }}>{money(orderData?.subtotalNet)}</td></tr>
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>Product discount</td><td style={{ padding: "8px 0", textAlign: "right", color: C.coral }}>− {money(orderData?.productDiscountNet)}</td></tr>
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>Order discount</td><td style={{ padding: "8px 0", textAlign: "right", color: C.coral }}>− {money(orderData?.orderDiscountNet)}</td></tr>
                        {/* The third bucket. It was missing here, which is exactly why
                            Subtotal − the two discounts + VAT did not equal Grand Total on any
                            order carrying a reward or a gift voucher. */}
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>Reward / voucher</td><td style={{ padding: "8px 0", textAlign: "right", color: C.coral }}>− {money(rewardNet(orderData))}</td></tr>
                        {/* The fulfilment fee ADDS — a curbside or drive-thru charge, not a
                            discount. Rendered only when there is one, so a Pickup order's
                            column is unchanged. Without it this column is short by the fee's
                            net and stops reaching Taxable amount, which is the same shape of
                            defect the Reward row above was added to fix. */}
                        {Number(orderData?.totalsBreakdown?.net?.fulfillmentFee) > 0 && (
                          <tr><td style={{ padding: "8px 0", color: C.textMed }}>Fulfilment fee</td><td style={{ padding: "8px 0", textAlign: "right", color: C.text }}>+ {money(orderData?.totalsBreakdown?.net?.fulfillmentFee)}</td></tr>
                        )}
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>Taxable amount</td><td style={{ padding: "8px 0", textAlign: "right", color: C.text }}>{money(orderData?.totalsBreakdown?.net?.taxableAmount)}</td></tr>
                        <tr><td style={{ padding: "8px 0", color: C.textMed }}>VAT</td><td style={{ padding: "8px 0", textAlign: "right", color: C.text }}>+ {money(orderData?.taxAmount)}</td></tr>
                        <tr style={{ borderTop: `2px solid ${C.border}` }}><td style={{ padding: "10px 0", color: C.text, fontWeight: 700 }}>Grand Total</td><td style={{ padding: "10px 0", textAlign: "right", color: C.coral, fontWeight: 800, fontSize: 15 }}>{money(orderData?.grandTotal)}</td></tr>
                      </tbody>
                    </table>
                  </Col>
                </Row>

                {Array.isArray(orderData?.orderDiscounts) && orderData.orderDiscounts.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <SectionTitle>Order-level Discounts</SectionTitle>
                    <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                      <table style={tbl.table}>
                        <thead>
                          <tr>
                            <th style={tbl.th}>Type</th>
                            <th style={tbl.th}>Name</th>
                            <th style={tbl.th}>Reference</th>
                            <th style={{ ...tbl.th, textAlign: "right" }}>Gross</th>
                            <th style={{ ...tbl.th, textAlign: "right" }}>Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderData.orderDiscounts.map((d, idx) => (
                            <tr key={idx} className="rpt-row">
                              <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>{d?.type}</td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{d?.name || "--"}</td>
                              <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{d?.reference || "--"}</td>
                              <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>− {money(d?.originalAmount)}</td>
                              <td style={{ ...tbl.td, textAlign: "right", color: C.coral, fontWeight: 700 }}>− {money(d?.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <ProgrammesSection orderData={orderData} />
              </Card>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter style={{ borderTop: `1px solid ${C.border}`, background: C.white }}>
        <Button color="secondary" onClick={handleClose} style={{ fontWeight: 600, borderRadius: 8 }}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OrderDetailModal;
